import React, { useState } from 'react';
import { Modal, Button, Text } from '@clockout-dev/design-elements';
import { InboxOutlined, LoadingOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { Progress } from 'antd';
import styles from '../styles/importListModal.module.scss';

// `onUpload`/`onPollStatus` are Segmentation.controller's own
// bulkUploadAccessList/getAccessListBulkStatus wrappers, not DataController
// called directly — each one checks admin:access_list:manage before making
// the request, so the scope guard lives on the write path itself rather
// than depending on every future render site remembering to check first.
function ImportListModal({ mode, isOpen, onClose, onSuccess, onUpload, onPollStatus }) {
  const [file, setFile] = useState(null);
  const [batchId, setBatchId] = useState(null);
  const [status, setStatus] = useState(null);
  const [progress, setProgress] = useState(0);
  const [summary, setSummary] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const resetModal = () => {
    setFile(null);
    setBatchId(null);
    setStatus(null);
    setProgress(0);
    setSummary(null);
    setError(null);
    setIsLoading(false);
  };

  const handleClose = () => {
    resetModal();
    onClose();
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    const files = e.dataTransfer?.files || e.currentTarget?.files;
    const csvFile = Array.from(files || []).find(f => f.name.endsWith('.csv'));

    if (!csvFile) {
      setError('Only CSV files are accepted');
      return;
    }

    setFile(csvFile);
    setError(null);
  };

  const handleUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await onUpload(formData);
      const batch_id = response?.batch_id;

      setBatchId(batch_id);
      setStatus('queued');
      setProgress(0);

      pollJobStatus(batch_id);
    } catch (err) {
      // The plain Error the controller throws when this session lacks
      // admin:access_list:manage has no `.response` (it never reaches
      // axios) — this path should be unreachable via the UI since the
      // trigger is hidden whenever that's true, but the message still
      // surfaces correctly if it somehow fires.
      setError(err.response?.data?.error?.message || err.message || 'Upload failed');
      setIsLoading(false);
    }
  };

  const pollJobStatus = async (batch_id) => {
    let attempts = 0;
    const maxAttempts = 120;

    const poll = async () => {
      try {
        const jobData = await onPollStatus(batch_id);

        setStatus(jobData.status);
        setProgress(jobData.progress_percent || 0);

        if (jobData.is_complete) {
          setSummary(jobData.summary);
          setIsLoading(false);

          if (jobData.status === 'completed' && onSuccess) {
            setTimeout(() => {
              onSuccess(jobData.summary);
            }, 1500);
          }
          return;
        }

        attempts++;
        if (attempts < maxAttempts) {
          setTimeout(poll, 1000);
        } else {
          setError('Job timeout');
          setIsLoading(false);
        }
      } catch (err) {
        setError('Failed to fetch job status');
        setIsLoading(false);
      }
    };

    poll();
  };

  const isProcessing = batchId && !summary;
  const isComplete = summary !== null;
  const isSuccess = isComplete && status === 'completed';

  return (
    <Modal
      open={isOpen}
      onCancel={handleClose}
      footer={null}
      title={`Import ${mode === 'whitelist' ? 'Allowlist' : 'Blocklist'}`}
    >
      <div className={styles.modalContent}>
        {!batchId ? (
          <>
            {/* Upload Section */}
            <div className={styles.uploadSection}>
              <div
                className={styles.dropZone}
                onDrop={handleFileDrop}
                onDragOver={(e) => e.preventDefault()}
                onClick={() => document.getElementById('importFileInput')?.click()}
              >
                <InboxOutlined className={styles.dropIcon} />
                <Text as="h4" className={styles.dropTitle}>
                  Drop your CSV file here
                </Text>
                <Text size="sm" className={styles.dropSubtitle}>
                  or click to browse
                </Text>
                <input
                  id="importFileInput"
                  type="file"
                  accept=".csv"
                  onChange={handleFileDrop}
                  className={styles.hiddenInput}
                />
              </div>

              {file && (
                <div className={styles.fileSelected}>
                  <div className={styles.fileName}>
                    <span>📄 {file.name}</span>
                  </div>
                  <Button
                    onClick={handleUpload}
                    disabled={!file || isLoading}
                    className={styles.uploadBtn}
                  >
                    {isLoading ? <LoadingOutlined /> : null}
                    {isLoading ? 'Processing...' : 'Import'}
                  </Button>
                </div>
              )}

              {error && !isProcessing && (
                <div className={styles.errorBox}>
                  <span className={styles.errorIcon}>⚠️</span>
                  <Text size="sm">{error}</Text>
                </div>
              )}

              {/* CSV Format Help */}
              <div className={styles.helpBox}>
                <Text as="h5" className={styles.helpTitle}>CSV Format Required</Text>
                <div className={styles.formatTable}>
                  <div className={styles.formatRow}>
                    <span className={styles.column}>member_id</span>
                    <span className={styles.column}>action</span>
                    <span className={styles.column}>reason (optional)</span>
                  </div>
                  <div className={styles.formatRow}>
                    <span className={styles.example}>john123</span>
                    <span className={styles.example}>ALLOW</span>
                    <span className={styles.example}>KYC verified</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            {/* Progress Section */}
            <div className={styles.progressSection}>
              <div className={styles.statusHeader}>
                {isProcessing && (
                  <>
                    <LoadingOutlined className={styles.processingIcon} />
                    <Text as="h4">Processing your import...</Text>
                  </>
                )}
                {isSuccess && (
                  <>
                    <CheckCircleOutlined className={styles.successIcon} />
                    <Text as="h4">Import completed successfully!</Text>
                  </>
                )}
                {isComplete && !isSuccess && (
                  <>
                    <CloseCircleOutlined className={styles.errorIcon} />
                    <Text as="h4">Import failed</Text>
                  </>
                )}
              </div>

              {isProcessing && (
                <div className={styles.progressBar}>
                  <Progress percent={progress} status="active" strokeColor="#1890ff" />
                  <Text size="xs" className={styles.progressText}>
                    {progress}% • {status}
                  </Text>
                </div>
              )}

              {summary && (
                <div className={styles.summaryBox}>
                  <div className={styles.summaryRow}>
                    <Text size="sm">Total processed:</Text>
                    <Text size="sm" className={styles.summaryValue}>{summary.processed}</Text>
                  </div>
                  <div className={styles.summaryRow}>
                    <Text size="sm">Successful:</Text>
                    <Text size="sm" className={styles.successValue}>{summary.successful}</Text>
                  </div>
                  <div className={styles.summaryRow}>
                    <Text size="sm">Failed:</Text>
                    <Text size="sm" className={summary.failed > 0 ? styles.errorValue : styles.successValue}>
                      {summary.failed}
                    </Text>
                  </div>
                  {summary.success_rate !== undefined && (
                    <div className={styles.summaryRow}>
                      <Text size="sm">Success rate:</Text>
                      <Text size="sm" className={styles.summaryValue}>{summary.success_rate}%</Text>
                    </div>
                  )}
                </div>
              )}

              {error && isProcessing && (
                <div className={styles.errorBox}>
                  <Text size="sm">{error}</Text>
                </div>
              )}
            </div>
          </>
        )}

        {/* Footer Actions */}
        <div className={styles.modalFooter}>
          {!batchId ? (
            <Button variant="secondary" onClick={handleClose}>
              Cancel
            </Button>
          ) : (
            <>
              {isComplete && (
                <Button onClick={handleClose} className={styles.closeBtn}>
                  {isSuccess ? 'Done' : 'Close'}
                </Button>
              )}
            </>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default ImportListModal;
